/** Route-driven API request detail dialog — /api-requests/:id/:tab?
 *
 * Each tab is deep-linkable (`info` | `body` | `response` | `reimport`); tab
 * switches navigate (so browser back/forward walks the tab history and the
 * Share button can copy an exact link), closing navigates back to the grid
 * with its query string intact. Reimport is manage-perm-gated — without the
 * permission the tab is hidden and a deep link falls back to Info.
 */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as apiRequestsApi from '@/api/apiRequests';
import { useAuth } from '@/auth/AuthProvider';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { TsValue } from '@/features/bgtasks/taskFormat';

import { StatusCodeChip } from '../apiRequestFormat';
import {
  integrationActions,
  statusCodeActions,
  tenantActions,
  type ApiRequestCtx,
} from '../apiRequestLinks';
import { AiDiagnosePanel } from '../diagnose/AiDiagnosePanel';
import { BodyTab } from './BodyTab';
import { InfoTab } from './InfoTab';
import { ReimportTab } from './ReimportTab';
import { ResponseTab } from './ResponseTab';

const TABS = [
  { id: 'info', label: 'Info' },
  { id: 'body', label: 'Body' },
  { id: 'response', label: 'Response' },
  { id: 'reimport', label: 'Reimport', perm: 'console_api_requests_manage' },
  { id: 'diagnose', label: 'Diagnose', perm: 'console_import_ai' },
] as const;

export interface ApiRequestDetailDialogProps {
  requestId: string;
  tab: string | undefined;
  onClose: () => void;
  onNavigateTab: (tab: string) => void;
}

export function ApiRequestDetailDialog({
  requestId,
  tab,
  onClose,
  onNavigateTab,
}: ApiRequestDetailDialogProps) {
  const auth = useAuth();
  const snackbar = useSnackbar();

  const visibleTabs = TABS.filter((t) => !('perm' in t) || auth.hasPerm(t.perm));
  const activeTab = visibleTabs.find((t) => t.id === tab)?.id ?? 'info';

  const detail = useQuery({
    queryKey: ['api-requests', 'detail', requestId],
    queryFn: () => apiRequestsApi.retrieve(requestId),
  });

  const copyLink = () => {
    void navigator.clipboard.writeText(
      `${window.location.origin}/api-requests/${requestId}/${activeTab}`,
    );
    snackbar.show('Link copied to clipboard.', 'success');
  };

  const d = detail.data;
  const ctx: ApiRequestCtx = { tenantId: d?.tenant?.id ?? null, createdTs: d?.created_ts ?? null };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: '88vh' } }}
    >
      <Box sx={{ px: 3, pt: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h6" component="h2" sx={{ fontFamily: 'monospace' }}>
              API request {requestId}
            </Typography>
            {d &&
              (d.status_code ? (
                <ValueActions chip actions={statusCodeActions(d.status_code, ctx)}>
                  <StatusCodeChip code={d.status_code} />
                </ValueActions>
              ) : (
                <StatusCodeChip code={d.status_code} />
              ))}
            <Tooltip title="Copy request id">
              <IconButton size="small" onClick={() => {
                void navigator.clipboard.writeText(requestId);
                snackbar.show('Request id copied.', 'success');
              }} aria-label="Copy request id">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy link to this tab">
              <IconButton size="small" onClick={copyLink} aria-label="Copy link to this tab">
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {d && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {d.tenant && (
                <>
                  <ValueActions actions={tenantActions(d.tenant, ctx)}>{d.tenant.name}</ValueActions>
                  {' · '}
                </>
              )}
              {d.integration ? (
                <ValueActions actions={integrationActions(d.integration, ctx)}>
                  {d.integration}
                </ValueActions>
              ) : (
                d.name
              )}
              {(d.integration || d.name) && ' · '}
              <TsValue value={d.created_ts} />
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, v: string) => onNavigateTab(v)}>
          {visibleTabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 3 }}>
        {detail.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {detail.error != null && (
          <Alert severity="error">Couldn’t load API request {requestId}.</Alert>
        )}
        {d && activeTab === 'info' && <InfoTab detail={d} />}
        {d && activeTab === 'body' && <BodyTab requestId={d.id} />}
        {d && activeTab === 'response' && <ResponseTab detail={d} />}
        {d && activeTab === 'reimport' && <ReimportTab detail={d} />}
        {d && activeTab === 'diagnose' && <AiDiagnosePanel id={d.id} uuid={d.uuid} />}
      </DialogContent>
    </Dialog>
  );
}
